import { ModeSelector } from "@/components/gamesetup/ModeSelector.js";
import { GameSettingsSelector } from "@/components/gamesetup/GameSettingsSelector.js";
import { AnimatePresence, motion } from "framer-motion";
import { GameMode } from "@rosette/ts/src/business/game/GameMode.js";
import { DifficultySelector } from "@/components/gamesetup/DifficultySelector.js";
import React, { useCallback, useRef } from "react";
import type { Ruleset } from "@rosette/ts/src/business/game/Ruleset.js";
import type { BotType } from "@rosette/ts/src/business/game/BotType.js";
import { fadeInOut } from "@/utils/fadeInOut.js";
import type { GameClientControls } from "@rosette/ts/src/business/GameClientControls.js";
import BackButton from "@/components/layout/BackButton.js";
import { MatchmakingControls } from "@/components/gamesetup/MatchmakingControls.js";
import { Path } from "@rosette/ts/src/util/Path.js";
import { GameSetupSubmit } from "@/components/gamesetup/GameSetupSubmit.js";
import { useLobbyManagerState } from "@/utils/api/useLobbyManagerState.js";
import { useGameAPI } from "@/components/api/GameAPIContext.js";
import type { GamePageParamValues } from "@/app/game/GamePageParamValues.js";
import type { GameSettings } from "@rosette/ts/src/royalur/model/GameSettings.js";
import { RulesetSelector } from "@/components/gamesetup/RulesetSelector.js";


interface GameSetupPanelProps {
    className?: string;
    clientControls: GameClientControls;
    paramValues: GamePageParamValues;
    setParamValues: (values: GamePageParamValues) => void;
    stripeTestMode: boolean;
}


export function GameSetupPanel({
    className, clientControls, paramValues, setParamValues, stripeTestMode,
}: GameSetupPanelProps) {
    const settings = paramValues.settings;
    const tab = paramValues.tab;

    const gameAPI = useGameAPI();
    const lobbyManagerState = useLobbyManagerState(gameAPI);

    const modeScrollRef = useRef<HTMLHeadingElement>(null);
    const rulesScrollRef = useRef<HTMLHeadingElement>(null);
    const botTypeScrollRef = useRef<HTMLHeadingElement>(null);

    const setMode = useCallback((mode: GameMode) => {
        setParamValues({
            settings: paramValues.settings.withMode(mode),
            tab: "rules",
        });
    }, [paramValues, setParamValues]);

    const setRuleset = useCallback((ruleset: Ruleset) => {
        setParamValues({
            settings: paramValues.settings.withRuleset(ruleset),
            tab: "rules",
        });
    }, [paramValues, setParamValues]);

    const setCustomGameSettings = useCallback((gameSettings: GameSettings) => {
        setParamValues({
            settings: paramValues.settings.withCustomGameSettings(gameSettings),
            tab: "rules",
        });
    }, [paramValues, setParamValues]);

    const setDifficulty = useCallback((botType: BotType) => {
        setParamValues({
            settings: paramValues.settings.withBotType(botType),
            tab: "rules",
        });
    }, [paramValues, setParamValues]);

    return (
        <section className={className}>
            <AnimatePresence mode="wait">
                {tab !== "rules" && (
                    <motion.div
                        key="mode"
                        {...fadeInOut()}>

                        <div ref={modeScrollRef}></div>
                        <ModeSelector
                            scrollRef={modeScrollRef}
                            selectedMode={settings.getMode()}
                            setSelectedMode={setMode} />
                    </motion.div>
                )}

                {tab === "rules" && (
                    <motion.div
                        key="settings"
                        {...fadeInOut()}>

                        <BackButton
                            className="mb-6 text-xl text-body"
                            label="Back"
                            onClick={() => {
                                setParamValues({
                                    settings: settings,
                                    tab: "mode",
                                });
                            }} />

                        {settings.getMode() === GameMode.ONLINE && (
                            <>
                                <h3 className="mt-4">
                                    Play Online
                                </h3>
                                <MatchmakingControls
                                    className="pt-4"
                                    clientControls={clientControls}
                                    getWatchLink={lobbyID => new Path(`/game/${lobbyID}`)}
                                    lobbyManagerState={lobbyManagerState} />
                            </>
                        )}

                        {settings.getMode() !== GameMode.ONLINE && (
                            <>
                                <h3 className="mt-4" ref={rulesScrollRef}>
                                    Choose the rules
                                </h3>
                                <RulesetSelector
                                    scrollRef={rulesScrollRef}
                                    selectedRuleset={settings.getRuleset()}
                                    setSelectedRuleset={setRuleset} />

                                {!settings.getRuleset().hasSettings() && (
                                    <>
                                        <h3 className="mt-4">
                                            Set up your custom rules
                                        </h3>
                                        <GameSettingsSelector
                                            customGameSettings={settings.getCustomGameSettings()}
                                            setCustomGameSettings={setCustomGameSettings} />
                                    </>
                                )}

                                {settings.getMode() === GameMode.COMPUTER && (
                                    <>
                                        <h3 className="mt-4" ref={botTypeScrollRef}>
                                            Choose your opponent
                                        </h3>
                                        <DifficultySelector
                                            scrollRef={botTypeScrollRef}
                                            settings={settings}
                                            setSelectedDifficulty={setDifficulty}
                                            lobbyManagerState={lobbyManagerState} />
                                    </>
                                )}

                                <GameSetupSubmit
                                    settings={settings}
                                    clientControls={clientControls}
                                    stripeTestMode={stripeTestMode} />
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
